/**
 * VibeguardSection — VibeGuard Engine (VGE) Findings
 *
 * Displays findings produced by the VibeGuard Engine: an AI agent firewall
 * that detects bash injection, file access violations, credential exposure,
 * network egress, and prompt injection patterns in scanned files.
 *
 * Patterns after RFIASection — collapsible cards, grouped by VGE category.
 *
 * @copyright 2026 Guardrail Security Technologies
 */

import { Shield, AlertTriangle, FileWarning, Eye, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface VGEFinding {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  category: string;
  title: string;
  description: string;
  location?: { file: string; line?: number; snippet?: string };
  cwe?: string;
  remediation?: string;
  confidence?: 'high' | 'medium' | 'low';
  source?: 'vge';
}

interface VibeguardSectionProps {
  findings: VGEFinding[];
  /** When true, all finding cards start expanded (print mode) */
  defaultExpanded?: boolean;
}

// ─── Category Metadata ────────────────────────────────────────────────────────

interface CategoryMeta {
  label: string;
  description: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

const CATEGORY_META: Record<string, CategoryMeta> = {
  injection: {
    label: 'Shell Injection',
    description: 'Pipe-to-shell, reverse shells, and command injection patterns',
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/20',
  },
  data_exposure: {
    label: 'Data Exposure',
    description: 'Sensitive file access and credential exposure patterns',
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/20',
  },
  data_exfiltration: {
    label: 'Data Exfiltration',
    description: 'Network egress and data exfiltration patterns',
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/20',
  },
  prompt_injection: {
    label: 'Prompt Injection',
    description: 'Malicious AI tool descriptions and jailbreak patterns',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/20',
  },
  malicious_code: {
    label: 'Malicious Code',
    description: 'Destructive MCP operations and malicious code patterns',
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/20',
  },
  authorization: {
    label: 'Authorization',
    description: 'Permission scope abuse',
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/20',
  },
  supply_chain: {
    label: 'Supply Chain',
    description: 'Dependency-related threats',
    color: 'text-sky-400',
    bgColor: 'bg-sky-500/10',
    borderColor: 'border-sky-500/20',
  },
  configuration: {
    label: 'Configuration',
    description: 'Misconfiguration and policy violations',
    color: 'text-zinc-400',
    bgColor: 'bg-zinc-500/10',
    borderColor: 'border-zinc-500/20',
  },
};

const DEFAULT_CATEGORY_META: CategoryMeta = {
  label: 'Security',
  description: 'Security pattern match',
  color: 'text-zinc-400',
  bgColor: 'bg-zinc-500/10',
  borderColor: 'border-zinc-500/20',
};

// ─── Severity Config ──────────────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<string, { color: string; bgColor: string; label: string }> = {
  critical: { color: 'text-red-400', bgColor: 'bg-red-500/10', label: 'Critical' },
  high: { color: 'text-orange-400', bgColor: 'bg-orange-500/10', label: 'High' },
  medium: { color: 'text-amber-400', bgColor: 'bg-amber-500/10', label: 'Medium' },
  low: { color: 'text-emerald-400', bgColor: 'bg-emerald-500/10', label: 'Low' },
  info: { color: 'text-sky-400', bgColor: 'bg-sky-500/10', label: 'Info' },
};

// ─── Category Counter ────────────────────────────────────────────────────────

function getCategoryCounts(findings: VGEFinding[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const f of findings) {
    counts.set(f.category, (counts.get(f.category) ?? 0) + 1);
  }
  return counts;
}

// ─── Individual Finding Card ──────────────────────────────────────────────────

function VGEFindingCard({
  finding,
  defaultExpanded = false,
}: {
  finding: VGEFinding;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const sevConfig = SEVERITY_CONFIG[finding.severity] ?? SEVERITY_CONFIG.info;
  const catMeta = CATEGORY_META[finding.category] ?? DEFAULT_CATEGORY_META;

  return (
    <div className="border border-zinc-700/50 rounded-lg overflow-hidden bg-zinc-900/30">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-3 hover:bg-zinc-800/30 transition-colors text-left"
      >
        <div
          className={cn(
            'px-2 py-0.5 rounded text-[10px] font-bold uppercase shrink-0',
            sevConfig.bgColor,
            sevConfig.color,
          )}
        >
          {sevConfig.label}
        </div>
        <span className="flex-1 text-sm font-medium text-zinc-200 truncate">{finding.title}</span>
        <span className={cn('text-[10px] hidden sm:inline shrink-0', catMeta.color)}>
          {catMeta.label}
        </span>
        {finding.location?.file && (
          <span className="text-[10px] text-zinc-500 font-mono hidden md:inline truncate max-w-[200px]">
            {finding.location.file}
            {finding.location.line ? `:${finding.location.line}` : ''}
          </span>
        )}
        <Eye
          className={cn('w-4 h-4 shrink-0 transition-transform', expanded ? 'text-zinc-300' : 'text-zinc-600')}
        />
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-zinc-800 bg-zinc-900/50">
          <div className="pt-3 space-y-3">
            <p className="text-sm text-zinc-300">{finding.description}</p>

            {finding.location?.file && (
              <div className="flex items-start gap-2 text-xs text-zinc-400 bg-zinc-800/50 px-3 py-2 rounded-md font-mono">
                <FileWarning className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span className="break-all">
                  {finding.location.file}
                  {finding.location.line ? `:${finding.location.line}` : ''}
                </span>
              </div>
            )}

            {finding.location?.snippet && (
              <div>
                <h5 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">
                  Matched
                </h5>
                <pre className="text-xs text-zinc-300 bg-zinc-800/60 border border-zinc-700/40 p-2.5 rounded-md overflow-x-auto whitespace-pre-wrap break-all">
                  {finding.location.snippet}
                </pre>
              </div>
            )}

            {finding.remediation && (
              <div>
                <h5 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">
                  Remediation
                </h5>
                <p className="text-sm text-emerald-300/80 bg-emerald-500/5 border border-emerald-500/10 p-2.5 rounded-md">
                  {finding.remediation}
                </p>
              </div>
            )}

            {finding.confidence && (
              <div className="flex items-center gap-2 pt-1">
                <span className="text-[10px] text-zinc-500">Confidence:</span>
                <span
                  className={cn(
                    'text-[10px] font-medium uppercase',
                    finding.confidence === 'high'
                      ? 'text-emerald-400'
                      : finding.confidence === 'medium'
                        ? 'text-amber-400'
                        : 'text-zinc-400',
                  )}
                >
                  {finding.confidence}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Category Group ────────────────────────────────────────────────────────────

function CategoryGroup({
  category,
  findings,
  defaultExpanded,
}: {
  category: string;
  findings: VGEFinding[];
  defaultExpanded: boolean;
}) {
  const [open, setOpen] = useState(true);
  const meta = CATEGORY_META[category] ?? DEFAULT_CATEGORY_META;

  return (
    <div className={cn('rounded-lg border overflow-hidden', meta.borderColor)}>
      {/* Group header */}
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
          meta.bgColor,
          'hover:brightness-110',
        )}
      >
        {open ? (
          <ChevronDown className={cn('w-4 h-4 shrink-0', meta.color)} />
        ) : (
          <ChevronRight className={cn('w-4 h-4 shrink-0', meta.color)} />
        )}
        <span className={cn('text-sm font-semibold', meta.color)}>{meta.label}</span>
        <span className="text-xs text-zinc-500 ml-1">{meta.description}</span>
        <span
          className={cn(
            'ml-auto text-xs font-bold px-2 py-0.5 rounded-full',
            meta.bgColor,
            meta.color,
          )}
        >
          {findings.length}
        </span>
      </button>

      {open && (
        <div className="divide-y divide-zinc-800/50">
          {findings.map((f) => (
            <VGEFindingCard key={f.id} finding={f} defaultExpanded={defaultExpanded} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

const VibeguardSection = ({ findings, defaultExpanded = false }: VibeguardSectionProps) => {
  const [showAll, setShowAll] = useState(defaultExpanded);
  const INITIAL_DISPLAY = 10;

  // Count by severity
  const bySeverity = {
    critical: findings.filter((f) => f.severity === 'critical').length,
    high: findings.filter((f) => f.severity === 'high').length,
    medium: findings.filter((f) => f.severity === 'medium').length,
    low: findings.filter((f) => f.severity === 'low').length,
    info: findings.filter((f) => f.severity === 'info').length,
  };

  // Group by category
  const categoryCounts = getCategoryCounts(findings);
  const categories = Array.from(categoryCounts.keys()).sort((a, b) => {
    // Sort by finding count descending
    return (categoryCounts.get(b) ?? 0) - (categoryCounts.get(a) ?? 0);
  });

  const findingsByCategory = new Map<string, VGEFinding[]>();
  for (const category of categories) {
    findingsByCategory.set(
      category,
      findings.filter((f) => f.category === category),
    );
  }

  // Flat list for simple pagination fallback
  // displayedFindings used for flat pagination — category groups handle their own display
  const hasMore = findings.length > INITIAL_DISPLAY;

  // Severity bar segments
  const total = findings.length;
  const severitySegments = [
    { key: 'critical', count: bySeverity.critical, color: 'bg-red-600' },
    { key: 'high', count: bySeverity.high, color: 'bg-orange-500' },
    { key: 'medium', count: bySeverity.medium, color: 'bg-amber-500' },
    { key: 'low', count: bySeverity.low, color: 'bg-emerald-500' },
    { key: 'info', count: bySeverity.info, color: 'bg-sky-500' },
  ].filter((s) => s.count > 0);

  return (
    <div className="space-y-6">
      {/* Overview Card */}
      <div className="rounded-xl border border-violet-500/20 bg-violet-500/5 p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-violet-400" />
            <div>
              <h3 className="text-lg font-semibold text-white">VibeGuard Engine</h3>
              <p className="text-sm text-zinc-400">
                AI agent firewall — behavioral pattern analysis across {categories.length} categor
                {categories.length !== 1 ? 'ies' : 'y'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg border border-violet-500/20 bg-violet-500/10">
            <AlertTriangle className="w-5 h-5 text-violet-400" />
            <span className="font-bold text-lg text-violet-400">{findings.length}</span>
            <span className="text-sm text-zinc-400">finding{findings.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Stats grid — per-category counts */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-4">
          {categories.slice(0, 5).map((cat) => {
            const meta = CATEGORY_META[cat] ?? DEFAULT_CATEGORY_META;
            const count = categoryCounts.get(cat) ?? 0;
            return (
              <div key={cat} className="bg-zinc-900/40 rounded-lg p-3 text-center">
                <div className={cn('text-2xl font-bold', meta.color)}>{count}</div>
                <div className="text-[10px] text-zinc-500 mt-1 truncate">{meta.label}</div>
              </div>
            );
          })}
        </div>

        {/* Severity breakdown bar */}
        {total > 0 && (
          <div className="mt-4 space-y-2">
            <div className="flex h-2.5 rounded-full overflow-hidden bg-zinc-800">
              {severitySegments.map((seg) => (
                <div
                  key={seg.key}
                  className={cn('transition-all', seg.color)}
                  style={{ width: `${(seg.count / total) * 100}%` }}
                  title={`${SEVERITY_CONFIG[seg.key]?.label}: ${seg.count}`}
                />
              ))}
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-zinc-400">
              {severitySegments.map((seg) => (
                <div key={seg.key} className="flex items-center gap-1.5">
                  <div className={cn('w-2.5 h-2.5 rounded-sm', seg.color)} />
                  <span>
                    {SEVERITY_CONFIG[seg.key]?.label}: {seg.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Findings grouped by category */}
      {findings.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider">
            Findings by Category ({findings.length} total)
          </h4>

          {/* Category groups */}
          <div className="space-y-3">
            {categories.map((cat) => {
              const catFindings = findingsByCategory.get(cat) ?? [];
              const displayFindings =
                showAll || defaultExpanded ? catFindings : catFindings.slice(0, INITIAL_DISPLAY);
              return (
                <CategoryGroup
                  key={cat}
                  category={cat}
                  findings={displayFindings}
                  defaultExpanded={defaultExpanded}
                />
              );
            })}
          </div>

          {hasMore && !showAll && (
            <button
              onClick={() => setShowAll(true)}
              className="w-full py-2 text-sm text-violet-400 hover:text-violet-300 transition-colors border border-zinc-700/50 rounded-lg hover:bg-zinc-800/30"
            >
              Show all {findings.length} findings
            </button>
          )}
        </div>
      )}

      {/* No findings — clean state shown only in print/defaultExpanded mode */}
      {findings.length === 0 && defaultExpanded && (
        <div className="flex items-center gap-3 p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-lg">
          <Shield className="w-5 h-5 text-emerald-400" />
          <p className="text-sm text-zinc-300">
            No VibeGuard patterns detected. All scanned files passed behavioral analysis.
          </p>
        </div>
      )}
    </div>
  );
};

export default VibeguardSection;
